import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { PlayCircle, MapPin, Calendar, ArrowUpRight } from 'lucide-react'
import { getMyEnrollments, type EnrollmentResult } from '@/services/enrollmentService'

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'กำลังเรียน',
  COMPLETED: 'เรียนจบแล้ว',
  REFUNDED: 'คืนเงินแล้ว',
}

const STATUS_DOT: Record<string, string> = {
  ACTIVE: 'bg-accent',
  COMPLETED: 'bg-emerald-500',
  REFUNDED: 'bg-rose-500',
}

const FILTERS = [
  { key: 'all', label: 'ทั้งหมด' },
  { key: 'ACTIVE', label: 'กำลังเรียน' },
  { key: 'COMPLETED', label: 'เรียนจบ' },
] as const

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' })

export default function MyCoursesTab() {
  const [enrollments, setEnrollments] = useState<EnrollmentResult[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['key']>('all')

  useEffect(() => {
    getMyEnrollments()
      .then(setEnrollments)
      .catch((err) => setError(err instanceof Error ? err.message : 'โหลดคอร์สของฉันไม่สำเร็จ'))
      .finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(
    () => (filter === 'all' ? enrollments : enrollments.filter((e) => e.status === filter)),
    [enrollments, filter]
  )

  return (
    <div>
      <p className="mb-6 text-sm text-[#69738A]">คอร์สทั้งหมดที่คุณลงทะเบียนไว้ ({enrollments.length})</p>

      {/* Filters — underline text tabs, not pills */}
      <div className="mb-6 flex gap-6 border-b border-[#E7E7ED]">
        {FILTERS.map((f) => {
          const active = filter === f.key
          const count = f.key === 'all' ? enrollments.length : enrollments.filter((e) => e.status === f.key).length
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`border-b-2 pb-3 text-sm transition-colors ${
                active ? 'border-accent font-medium text-accent' : 'border-transparent text-[#69738A] hover:text-[#19172A]'
              }`}
            >
              {f.label} <span className="text-xs text-[#69738A]">({count})</span>
            </button>
          )
        })}
      </div>

      {loading ? (
        <p className="text-sm text-[#69738A]">กำลังโหลด...</p>
      ) : error ? (
        <p className="text-sm text-rose-500">{error}</p>
      ) : filtered.length === 0 ? (
        <div className="border border-[#E7E7ED] bg-white p-12 text-center" style={{ borderRadius: 6 }}>
          <p className="mb-4 text-[#69738A]">
            {filter === 'all' ? 'คุณยังไม่ได้ลงทะเบียนคอร์สใดเลย' : 'ไม่มีคอร์สในหมวดนี้'}
          </p>
          <Link to="/courses" className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline">
            ไปเลือกดูคอร์สทั้งหมด <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      ) : (
        <div className="divide-y divide-[#E7E7ED] border border-[#E7E7ED] bg-white" style={{ borderRadius: 6 }}>
          {filtered.map((en) => (
            <Link
              key={en.enrollment_id}
              to={`/courses/${en.course.slug}`}
              className="group flex gap-5 p-5 transition-colors hover:bg-[#F7F6FB]"
            >
              <div className="flex h-20 w-32 shrink-0 items-center justify-center overflow-hidden bg-[#F0EDFF]" style={{ borderRadius: 4 }}>
                {en.course.thumbnail_url ? (
                  <img src={en.course.thumbnail_url} alt={en.course.title} className="h-full w-full object-cover" />
                ) : (
                  <PlayCircle className="h-7 w-7 text-accent/50" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-1.5 flex items-center gap-1.5">
                  <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${STATUS_DOT[en.status] || 'bg-[#69738A]'}`} />
                  <span className="text-xs font-medium uppercase tracking-wide text-[#69738A]">
                    {STATUS_LABEL[en.status] || en.status}
                  </span>
                  {en.enrolled_by_company_name && (
                    <span className="ml-1 bg-[#F0EDFF] px-1.5 py-0.5 text-[11px] font-medium text-accent" style={{ borderRadius: 3 }}>
                      {en.enrolled_by_company_name} ซื้อให้
                    </span>
                  )}
                </div>
                <p className="mb-2 line-clamp-2 text-[15px] font-medium leading-snug text-[#19172A] transition-colors group-hover:text-accent">
                  {en.course.title}
                </p>

                {en.course.course_format === 'ONLINE' ? (
                  <div className="max-w-xs">
                    <div className="h-1 w-full bg-[#E7E7ED]">
                      <div className="h-full bg-accent" style={{ width: `${en.progress_percent}%` }} />
                    </div>
                    <p className="mt-1.5 text-xs text-[#69738A]">{en.progress_percent}% เรียนจบแล้ว</p>
                  </div>
                ) : en.batch ? (
                  <div className="space-y-1 text-xs text-[#69738A]">
                    <p className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {formatDate(en.batch.start_date)}</p>
                    {en.batch.venue_name && (
                      <p className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {en.batch.venue_name}</p>
                    )}
                  </div>
                ) : null}
              </div>
              <ArrowUpRight className="h-4 w-4 shrink-0 self-start text-[#69738A] transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-accent" />
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
